import { FetchState } from "../../entities/FetchState";
import { IReportDataObject, emptyReportDataObject } from "../../entities/reports/IReportData";
import { Reducer } from "redux";
import { all, call, fork, put, takeEvery } from "redux-saga/effects";
import Either from "../../utils/Either";
import { ActionCreators as Navigation } from "../navigate"
import {callApiNoResult, callApiWithResult, ICallNoResult, ICallResult, IError} from "../../utils/callApi";

/**********************************************************
 * API Data
 **********************************************************/
export interface IDataCity {
    cityId: string;
    cityName: string | null;
    postalCode: string | null;
    province: string | null;
}

export interface IData {
    id: string;
    name: string;
    address: string;
    active: boolean;
    salutation: string | null;
    contactpersonLastname: string | null;
    contactpersonFirstname: string | null;
    contactpersonFunction: string | null;
    contactpersonEmail: string | null;
    contactpersonEmail2: string | null;
    contactpersonMobile: string | null;
    contactpersonTelephone: string | null;
    contactperson2: string | null;
    contactDetails: string | null;
    appointmentRequired: boolean;
    availability: string | null;
    conversationSpace: string | null;
    internet: string | null;
    drinking: string | null;
    key: string | null;
    alarm: string | null;
    heating: string | null;
    flipover: string | null;
    inactiveDate: string | null; // Date as ISO string
    contractAvailable: boolean;
    contractSaved: boolean;
    contractDate: string | null; // Date as ISO string
    noContractReason: string | null;
    contactpointPlus: string | null;
    city: IDataCity | null;
    appointmentInformation: string | null;
}

export const defaultContactpointItem: IData = {
    id: '',
    name: '',
    address: '',
    active: true,
    salutation: null,
    contactpersonLastname: null,
    contactpersonFirstname: null,
    contactpersonFunction: null,
    contactpersonEmail: null,
    contactpersonEmail2: null,
    contactpersonMobile: null,
    contactpersonTelephone: null,
    contactperson2: null,
    contactDetails: null,
    appointmentRequired: false,
    availability: null,
    conversationSpace: null,
    internet: null,
    drinking: null,
    key: null,
    alarm: null,
    heating: null,
    flipover: null,
    inactiveDate: null,
    contractAvailable: false,
    contractSaved: false,
    contractDate: null,
    noContractReason: null,
    contactpointPlus: null,
    city: null,
    appointmentInformation: null
};

// Make a default ContactpointDetail with empty values
export const defaultContactpointDetail = emptyReportDataObject<IData>(defaultContactpointItem)

/**********************************************************
 * State
 **********************************************************/

export interface IState {
    Item: IReportDataObject<IData>
    UpdateItem: IReportDataObject<IData>
    DeleteItem: IReportDataObject<IData>
}
const InitialState: IState = {
    Item: defaultContactpointDetail,
    UpdateItem: defaultContactpointDetail,
    DeleteItem: defaultContactpointDetail
}

/**********************************************************
 * Action Types
 **********************************************************/
export enum ActionTypes {
    // Fetch action types
    FetchData = "contactpoint.ContactpointItem Fetch",
    FetchDataSuccess = "contactpoint.ContactpointItem FetchSuccess",
    FetchDataFailed = "contactpoint.ContactpointItem FetchFailed",
    
    // Update action types
    UpdateData = "contactpoint.ContactpointItem Update",
    UpdateDataSuccess = "contactpoint.ContactpointItem UpdateSuccess",
    UpdateDataFailed = "contactpoint.ContactpointItem UpdateFailed",
    
    // Delete action types
    DeleteData = "contactpoint.ContactpointItem Delete",
    DeleteDataSuccess = "contactpoint.ContactpointItem DeleteSuccess",
    DeleteDataFailed = "contactpoint.ContactpointItem DeleteFailed",
    
    // Enable action types
    EnableData = "contactpoint.ContactpointItem Enable",
    EnableDataSuccess = "contactpoint.ContactpointItem EnableSuccess",
    EnableDataFailed = "contactpoint.ContactpointItem EnableFailed",
    
    // Disable action types
    DisableData = "contactpoint.ContactpointItem Disable",
    DisableDataSuccess = "contactpoint.ContactpointItem DisableSuccess",
    DisableDataFailed = "contactpoint.ContactpointItem DisableFailed"
}

/**********************************************************
 * Actions & Creators
 **********************************************************/
export interface IFetchData {
    type: ActionTypes.FetchData,
    payload: { id: string }
}
export interface IFetchDataSuccess {
    type: ActionTypes.FetchDataSuccess,
    payload: { data: IData }
}
export interface IFetchDataFailed {
    type: ActionTypes.FetchDataFailed,
    payload: { errorMessage: string }
}

// Update Actions
export interface IUpdateData {
    type: ActionTypes.UpdateData,
    payload: { data: IData }
}
export interface IUpdateDataSuccess {
    type: ActionTypes.UpdateDataSuccess,
    payload: { id: string }
}
export interface IUpdateDataFailed {
    type: ActionTypes.UpdateDataFailed,
    payload: { errorMessage: string }
}

// Delete Actions
export interface IDeleteData {
    type: ActionTypes.DeleteData,
    payload: { id: string }
}
export interface IDeleteDataSuccess {
    type: ActionTypes.DeleteDataSuccess,
    payload: { id: string }
}
export interface IDeleteDataFailed {
    type: ActionTypes.DeleteDataFailed,
    payload: { errorMessage: string }
}

// Enable Actions
export interface IEnableData {
    type: ActionTypes.EnableData,
    payload: { id: string }
}
export interface IEnableDataSuccess {
    type: ActionTypes.EnableDataSuccess,
    payload: { id: string }
}
export interface IEnableDataFailed {
    type: ActionTypes.EnableDataFailed,
    payload: { errorMessage: string }
}

// Disable Actions
export interface IDisableData {
    type: ActionTypes.DisableData,
    payload: { id: string }
}
export interface IDisableDataSuccess {
    type: ActionTypes.DisableDataSuccess,
    payload: { id: string }
}
export interface IDisableDataFailed {
    type: ActionTypes.DisableDataFailed,
    payload: { errorMessage: string }
}

type Actions = IFetchData | IFetchDataSuccess | IFetchDataFailed | IUpdateData | IUpdateDataSuccess | IUpdateDataFailed | IDeleteData | IDeleteDataSuccess | IDeleteDataFailed | IEnableData | IEnableDataSuccess | IEnableDataFailed | IDisableData | IDisableDataSuccess | IDisableDataFailed;

export class ActionCreators {
    public static FetchData(id: string): IFetchData {
        return { 
            type: ActionTypes.FetchData, 
            payload: { id } 
        };
    }
    public static FetchDataSuccess(newData: IData): IFetchDataSuccess {
        return {
            type: ActionTypes.FetchDataSuccess,
            payload: { data: newData }
        };
    }
    public static FetchDataFailed(err: string): IFetchDataFailed {
        return {
            type: ActionTypes.FetchDataFailed,
            payload: { errorMessage: err }
        };
    }
    
    public static UpdateData(data: IData): IUpdateData {
        return { 
            type: ActionTypes.UpdateData, 
            payload: { data } 
        };
    }
    public static UpdateDataSuccess(id: string): IUpdateDataSuccess {
        return {
            type: ActionTypes.UpdateDataSuccess,
            payload: { id }
        };
    }
    public static UpdateDataFailed(err: string): IUpdateDataFailed {
        return {
            type: ActionTypes.UpdateDataFailed,
            payload: { errorMessage: err }
        };
    }
    
    public static DeleteData(id: string): IDeleteData {
        return { 
            type: ActionTypes.DeleteData, 
            payload: { id } 
        };
    }
    public static DeleteDataSuccess(id: string): IDeleteDataSuccess {
        return {
            type: ActionTypes.DeleteDataSuccess,
            payload: { id }
        };
    }
    public static DeleteDataFailed(err: string): IDeleteDataFailed {
        return {
            type: ActionTypes.DeleteDataFailed,
            payload: { errorMessage: err }
        };
    }
    
    public static EnableData(id: string): IEnableData {
        return { 
            type: ActionTypes.EnableData, 
            payload: { id } 
        };
    }
    public static EnableDataSuccess(id: string): IEnableDataSuccess {
        return {
            type: ActionTypes.EnableDataSuccess,
            payload: { id }
        };
    }
    public static EnableDataFailed(err: string): IEnableDataFailed {
        return {
            type: ActionTypes.EnableDataFailed,
            payload: { errorMessage: err }
        };
    }
    
    public static DisableData(id: string): IDisableData {
        return { 
            type: ActionTypes.DisableData, 
            payload: { id } 
        };
    }
    public static DisableDataSuccess(id: string): IDisableDataSuccess {
        return {
            type: ActionTypes.DisableDataSuccess,
            payload: { id }
        };
    }
    public static DisableDataFailed(err: string): IDisableDataFailed {
        return {
            type: ActionTypes.DisableDataFailed,
            payload: { errorMessage: err }
        };
    }
}

/**********************************************************
 * Reducer
 **********************************************************/
export const Reducers: Reducer<IState, Actions> = (state = InitialState, action) => {
    switch (action.type) {
        case ActionTypes.FetchData:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.FetchDataSuccess:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Success,
                    data: action.payload.data,
                    fetchDate: new Date()
                }
            };
        case ActionTypes.FetchDataFailed:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
            
        case ActionTypes.UpdateData:
            return { 
                ...state, 
                UpdateItem: {
                    ...state.UpdateItem,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.UpdateDataSuccess:
            return { 
                ...state, 
                UpdateItem: {
                    ...state.UpdateItem,
                    state: FetchState.Success,
                    data: {
                        ... state.UpdateItem.data,
                        id: action.payload.id
                    },
                    fetchDate: new Date()
                },
                Item: {
                    ...InitialState.Item,
                }
            };
        case ActionTypes.UpdateDataFailed:
            return { 
                ...state, 
                UpdateItem: {
                    ...state.UpdateItem,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
            
        case ActionTypes.DeleteData:
            return { 
                ...state, 
                DeleteItem: {
                    ...state.DeleteItem,
                    state: FetchState.Busy
                },
                Item: {
                    ...InitialState.Item,
                }
            };

        case ActionTypes.DeleteDataSuccess:
            return { 
                ...state, 
                DeleteItem: {
                    ...state.DeleteItem,
                    state: FetchState.Success,
                    data: {
                        ... state.DeleteItem.data,
                        id: action.payload.id
                    },
                    fetchDate: new Date()
                }
            };

        case ActionTypes.DeleteDataFailed:
            return { 
                ...state, 
                DeleteItem: {
                    ...state.DeleteItem,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
            
        // Enable cases
        case ActionTypes.EnableData:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.EnableDataSuccess:
            return { 
                ...state,
                Item: {
                    ...InitialState.Item,
                }
            };
        case ActionTypes.EnableDataFailed:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
            
        // Disable cases
        case ActionTypes.DisableData:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.DisableDataSuccess:
            return { 
                ...state,
                Item: {
                    ...InitialState.Item,
                }
            };
        case ActionTypes.DisableDataFailed:
            return { 
                ...state, 
                Item: {
                    ...state.Item,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };

        default:
            return state;
    }
}

/**********************************************************
 * Sagas
 **********************************************************/

function* handleIFetchData(action: IFetchData) {
    try {
        const response : Either<IError, ICallResult<IData>> = yield call(callApiWithResult, 'GET', `/v1/contactpoint/${action.payload.id}`);
        yield response
            .FlatMap(success => put(ActionCreators.FetchDataSuccess(success.result) as any))
            .Reduce(error => put(ActionCreators.FetchDataFailed(error.text) as any))
    } catch (err) {
        console.error('ContactpointItem.handleIFetchData: ', err, action)
        yield put(ActionCreators.FetchDataFailed(JSON.stringify(err)))
    }
}

function* handleIUpdateData(action: IUpdateData) {
    try {
        const contactpointUpdateDto = {
            cityStreamId: action.payload.data.city?.cityId,
            name: action.payload.data.name,
            address: action.payload.data.address,
            salutation: action.payload.data.salutation,
            contactpersonLastname: action.payload.data.contactpersonLastname,
            contactpersonFirstname: action.payload.data.contactpersonFirstname,
            contactpersonFunction: action.payload.data.contactpersonFunction,
            contactpersonEmail: action.payload.data.contactpersonEmail,
            contactpersonEmail2: action.payload.data.contactpersonEmail2,
            contactpersonMobile: action.payload.data.contactpersonMobile,
            contactpersonTelephone: action.payload.data.contactpersonTelephone,
            contactperson2: action.payload.data.contactperson2,
            contactDetails: action.payload.data.contactDetails,
            appointmentRequired: action.payload.data.appointmentRequired,
            availability: action.payload.data.availability,
            appointmentInformation: action.payload.data.appointmentInformation,
            conversationSpace: action.payload.data.conversationSpace,
            internet: action.payload.data.internet,
            drinking: action.payload.data.drinking,
            key: action.payload.data.key,
            alarm: action.payload.data.alarm,
            heating: action.payload.data.heating,
            flipover: action.payload.data.flipover,
            contractAvailable: action.payload.data.contractAvailable,
            contractSaved: action.payload.data.contractSaved,
            contractDate: action.payload.data.contractDate,
            noContractReason: action.payload.data.noContractReason,
            contactpointPlus: action.payload.data.contactpointPlus,
        };

        const response : Either<IError, ICallNoResult> = yield call(callApiNoResult, 'PUT', `/v1/contactpoint/${action.payload.data.id}`, contactpointUpdateDto);
        yield response
            .FlatMap(success => put(ActionCreators.UpdateDataSuccess(action.payload.data.id) as any))
            .Reduce(error => put(ActionCreators.UpdateDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.UpdateDataFailed(JSON.stringify(err)))
    }
}

function* handleIDeleteData(action: IDeleteData) {
    try {
        const response : Either<IError, ICallNoResult> = yield call(callApiNoResult, 'DELETE', `/v1/contactpoint/${action.payload.id}`);
        yield response
            .FlatMap(success => put(ActionCreators.DeleteDataSuccess(action.payload.id) as any))
            .Reduce(error => put(ActionCreators.DeleteDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.DeleteDataFailed(JSON.stringify(err)))
    }
}

function* handleIEnableData(action: IEnableData) {
    try {
        const response : Either<IError, ICallNoResult> = yield call(callApiNoResult, 'PUT', `/v1/contactpoint/${action.payload.id}/enable`);
        yield response
            .FlatMap(success => put(ActionCreators.EnableDataSuccess(action.payload.id) as any))
            .Reduce(error => put(ActionCreators.EnableDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.EnableDataFailed(JSON.stringify(err)))
    }
}

function* handleIDisableData(action: IDisableData) {
    try {
        const response : Either<IError, ICallNoResult> = yield call(callApiNoResult, 'PUT', `/v1/contactpoint/${action.payload.id}/disable`);
        yield response
            .FlatMap(success => put(ActionCreators.DisableDataSuccess(action.payload.id) as any))
            .Reduce(error => put(ActionCreators.DisableDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.DisableDataFailed(JSON.stringify(err)))
    }
}

function* handleIUpdateDataSuccess(action: IUpdateDataSuccess) {
    yield put(Navigation.ToContactpointDetail(action.payload.id))
}

function* handleIDeleteDataSuccess(action: IDeleteDataSuccess) {
    yield put(Navigation.ToContactpointList())
}

function* handleIEnableDataSuccess(action: IEnableDataSuccess) {
    // Refresh the contactpoint data after enabling
    yield put(ActionCreators.FetchData(action.payload.id))
}

function* handleIDisableDataSuccess(action: IDisableDataSuccess) {
    // Refresh the contactpoint data after disabling
    yield put(ActionCreators.FetchData(action.payload.id))
}

function* watchAll() {
    yield takeEvery(ActionTypes.FetchData, handleIFetchData)
    yield takeEvery(ActionTypes.UpdateData, handleIUpdateData)
    yield takeEvery(ActionTypes.UpdateDataSuccess, handleIUpdateDataSuccess)
    yield takeEvery(ActionTypes.DeleteData, handleIDeleteData)
    yield takeEvery(ActionTypes.DeleteDataSuccess, handleIDeleteDataSuccess)
    yield takeEvery(ActionTypes.EnableData, handleIEnableData)
    yield takeEvery(ActionTypes.DisableData, handleIDisableData)
    yield takeEvery(ActionTypes.EnableDataSuccess, handleIEnableDataSuccess)
    yield takeEvery(ActionTypes.DisableDataSuccess, handleIDisableDataSuccess)
}

export function* sagas() {
    yield all([fork(watchAll)]);
}
